/* eslint-disable no-restricted-imports */
import type { BeforeErrorHook } from 'ky';

import { ApiError, statusToErrorCode } from '~/api/errors';
import type { ApiErrorResponse } from '~/types/api.types';

/**
 * Ky beforeError hook — normalises HTTPError into a typed ApiError.
 * Parses the response body for structured error details, supporting both the
 * legacy flat shape and the contract envelope (`{ error: { code, fields } }`).
 */
export const errorInterceptor: BeforeErrorHook = async (error) => {
  const { response, request, options } = error;

  let body: Partial<ApiErrorResponse> = {};
  try {
    body = (await response.clone().json()) as Partial<ApiErrorResponse>;
  } catch {
    // Response body is not JSON — use defaults
  }

  const code = statusToErrorCode(response.status);
  // FastAPI `detail` may be a bare string or a structured `{ error_code, message }`
  // object (integration-service scoped-import guards use the latter). Normalise
  // both so `detail`'s message/code join the coalescing chains below.
  const detail = body.detail;
  const detailObject = detail && typeof detail === 'object' ? detail : null;
  const detailMessage = typeof detail === 'string' ? detail : (detailObject?.message ?? undefined);
  // `message` may be a plain string or an array of validation messages (e.g. 422);
  // fall back to the FastAPI `detail` message when the top-level `message` is
  // absent, then to the contract envelope's `error.details` prose (the archive
  // endpoints' 409s carry their explanation there and nowhere else).
  const rawMessage = body.message ?? detailMessage ?? body.error?.details ?? undefined;
  // The experiment/workflow service returns the actionable specifics in `errors[]`
  // alongside a generic top-level `message` (e.g. "Conflict" / "Validation failed").
  // Prefer these so the UI surfaces something the user can act on rather than the
  // bare status word. Absent on other services, so their behaviour is unchanged.
  const detailErrors: string[] = Array.isArray(body.errors)
    ? body.errors.filter((e): e is string => typeof e === 'string' && e.trim() !== '')
    : [];
  const messages: string[] =
    detailErrors.length > 0
      ? detailErrors
      : Array.isArray(rawMessage)
        ? rawMessage
        : rawMessage
          ? [rawMessage]
          : [];
  const message = messages.join('\n') || 'An unexpected error occurred';
  const fieldErrors = body.fieldErrors;
  // Prefer the contract §1.6 envelope; fall back to the legacy top-level
  // forms (`error_code` snake_case, then `code` camelCase) so routes that
  // still send the flat shape — e.g. integration-service DELETE on a
  // pipeline-bound connection responds with top-level `error_code` —
  // surface a usable `serverCode` instead of `undefined`. FastAPI `detail`
  // objects (scoped import) are the final fallback.
  const serverCode = body.error?.code ?? body.error_code ?? body.code ?? detailObject?.error_code;
  const fields = body.error?.fields ?? undefined;
  // Support reference from the flat `details` object (delivery read endpoints'
  // `DELIVERY_READ_FAILED`); a plain string tag the server also writes to its log.
  const reference =
    body.details && typeof body.details.reference === 'string' && body.details.reference !== ''
      ? body.details.reference
      : undefined;

  return new ApiError(
    code,
    response.status,
    message,
    response,
    request,
    options,
    fieldErrors,
    serverCode,
    fields,
    messages,
    reference,
    body,
  );
};
